// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useState, useEffect, useRef } from 'react';
import { VideoUploadService } from './videoUploadService';
import { useProctoring } from '../../../hooks/useProctoring';
import './VideoProctoring.css';

interface VideoProctoringProps {
  sessionId: string;
  interviewId: string;
}

const VideoProctoring: React.FC<VideoProctoringProps> = ({ sessionId, interviewId }) => {
  // State
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const [violations, setViolations] = useState<Array<{ type: string; message: string; timestamp: string }>>([]);

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const uploadServiceRef = useRef<VideoUploadService | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingSessionId = sessionId || interviewId;

  /**
   * Initialize video capture on mount
   */
  useEffect(() => {
    initializeVideoCapture();

    return () => {
      cleanup();
    };
  }, []);

  /**
   * Start recording when stream is ready
   */
  useEffect(() => {
    if (stream && !isRecording) {
      startRecording();
    }
  }, [stream, isRecording]);

  /**
   * Proctoring - Face detection and tab switch monitoring
   */
  const proctoringConfig = React.useMemo(
    () => ({
      sessionId: interviewId,
      enabled: true, // Always enabled - tab switch works without video
      onViolation: (violation: any) => {
        console.warn('[VideoProctoring] Proctoring violation:', violation.type, violation.message);
        // Add to violations list (keep last 3)
        setViolations((prev) => [...prev.slice(-2), violation]);

        // Auto-dismiss after 5 seconds
        setTimeout(() => {
          setViolations((prev) => prev.filter((v) => v.timestamp !== violation.timestamp));
        }, 5000);
      }
    }),
    [interviewId]
  );

  useProctoring(videoRef.current, proctoringConfig);

  /**
   * Ensure video element plays when stream is available
   */
  useEffect(() => {
    if (stream && videoRef.current) {
      const videoElement = videoRef.current;

      if (videoElement.srcObject !== stream) {
        videoElement.srcObject = stream;
      }

      const ensurePlayback = async (): Promise<void> => {
        if (videoElement.paused) {
          try {
            await videoElement.play();
            console.log('[VideoRecording] Video playback ensured');
          } catch (err) {
            console.error('[VideoRecording] Failed to ensure playback:', err);
          }
        }
      };

      ensurePlayback();

      const handlePause = (): void => {
        if (stream && videoElement.srcObject === stream) {
          console.log('[VideoRecording] Video paused unexpectedly, resuming...');
          ensurePlayback();
        }
      };

      videoElement.addEventListener('pause', handlePause);

      return () => {
        videoElement.removeEventListener('pause', handlePause);
      };
    }
  }, [stream]);

  /**
   * Cleanup when user navigates away or closes tab
   */
  useEffect(() => {
    const handleBeforeUnload = (): void => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [stream]);

  /**
   * Initialize video capture and services
   */
  const initializeVideoCapture = async (): Promise<void> => {
    try {
      setIsLoading(true);
      console.log('[VideoRecording] Requesting camera access...');

      // Request camera and microphone permission so recruiter playback includes candidate audio.
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 15 },
          facingMode: 'user'
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      console.log('[VideoRecording] Camera access granted');
      console.log('[VideoRecording] Video tracks:', mediaStream.getVideoTracks().length);
      const audioTracks = mediaStream.getAudioTracks();
      console.log('[VideoRecording] Audio tracks:', audioTracks.length);
      if (!audioTracks.length) {
        console.warn('[VideoRecording] Recording stream does not include microphone audio');
      }

      // Set video stream and wait for it to be ready
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;

        // Wait for video metadata to load
        await new Promise<void>((resolve, reject) => {
          if (!videoRef.current) {
            reject(new Error('Video element not available'));
            return;
          }

          const videoElement = videoRef.current;

          videoElement.onloadedmetadata = () => {
            console.log('[VideoRecording] Video metadata loaded');
            console.log('[VideoRecording] Video dimensions:', videoElement.videoWidth, 'x', videoElement.videoHeight);
            setVideoReady(true);
            resolve();
          };

          videoElement.onerror = (e) => {
            console.error('[VideoRecording] Video element error:', e);
            reject(new Error('Video element failed to load'));
          };

          setTimeout(() => reject(new Error('Video metadata load timeout')), 5000);
        });

        // Explicitly play the video
        try {
          await videoRef.current.play();
          console.log('[VideoRecording] Video playback started');
        } catch (playError) {
          console.error('[VideoRecording] Video play error:', playError);
          setTimeout(async () => {
            try {
              await videoRef.current?.play();
              console.log('[VideoRecording] Video playback started (retry)');
            } catch (retryError) {
              console.error('[VideoRecording] Video play retry failed:', retryError);
            }
          }, 100);
        }
      }

      streamRef.current = mediaStream;
      setStream(mediaStream);

      // Initialize video upload service only
      uploadServiceRef.current = new VideoUploadService();

      setIsLoading(false);
      console.log('[VideoRecording] ✅ Video recording initialized successfully');
    } catch (error: any) {
      setIsLoading(false);
      console.error('[VideoRecording] Failed to initialize video capture:', error);
      console.error('[VideoRecording] Error name:', error.name);
      console.error('[VideoRecording] Error message:', error.message);

      setError('Camera access denied. Interview will continue without video recording.');

      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        console.warn('[VideoRecording] Camera permission denied by user');
      } else if (error.name === 'NotFoundError') {
        console.warn('[VideoRecording] No camera found on device');
      } else if (error.name === 'NotReadableError') {
        console.warn('[VideoRecording] Camera is already in use by another application');
      }
    }
  };

  /**
   * Start video recording with chunked upload
   */
  const startRecording = (): void => {
    if (stream && uploadServiceRef.current && !isRecording && recordingSessionId) {
      uploadServiceRef.current.startRecording(stream, recordingSessionId);
      setIsRecording(true);
      console.log('[VideoRecording] Recording started');
    }
  };

  /**
   * Cleanup resources - stops camera and recording
   */
  const cleanup = async (): Promise<void> => {
    console.log('[VideoRecording] Starting cleanup...');

    // Stop recording and upload final chunk
    if (uploadServiceRef.current) {
      try {
        await uploadServiceRef.current.stopRecording();
        console.log('[VideoRecording] Recording stopped');
      } catch (error) {
        console.error('[VideoRecording] Error stopping recording:', error);
      }
      uploadServiceRef.current = null;
    }

    // Stop video stream tracks (turns off camera light)
    const activeStream = streamRef.current || stream;
    if (activeStream) {
      activeStream.getTracks().forEach((track) => {
        console.log('[VideoRecording] Stopping track:', track.kind, track.label);
        track.stop();
      });
      streamRef.current = null;
      setStream(null);
    }

    // Clear video element
    if (videoRef.current) {
      videoRef.current.onloadedmetadata = null;
      videoRef.current.onerror = null;
      videoRef.current.oncanplay = null;
      videoRef.current.onplay = null;
      videoRef.current.onloadeddata = null;
      videoRef.current.pause();
      videoRef.current.srcObject = null;
      videoRef.current.load();
      console.log('[VideoRecording] Video element cleared');
    }

    // Reset state
    setIsRecording(false);
    setIsLoading(false);
    setVideoReady(false);

    console.log('[VideoRecording] ✅ Cleanup complete - camera released');
  };

  // Don't render if error occurred (graceful degradation)
  if (error) {
    return null;
  }

  // Don't render until video stream is ready
  if (!stream) {
    return null;
  }

  return (
    <div className="video-recording-container">
      <div className="video-preview-wrapper">
        {/* Video feed */}
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="video-preview"
          onCanPlay={() => {
            console.log('[VideoRecording] Video can play');
            setVideoReady(true);
          }}
          onPlay={() => {
            console.log('[VideoRecording] Video playing');
          }}
          onLoadedData={() => {
            console.log('[VideoRecording] Video data loaded');
          }}
        />

        {/* Loading indicator */}
        {isLoading && (
          <div className="recording-loading">
            <div className="recording-spinner" />
            <span>Starting camera...</span>
          </div>
        )}

        {/* Corner badge - "Cam" */}
        <div className="recording-corner-badge">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M23 7l-7 5 7 5V7z" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
          </svg>
          Cam
        </div>

        {/* Footer with LIVE indicator */}
        <div className="video-preview-footer">
          <div className="recording-live-tag">
            <span className="recording-dot" />
            LIVE
          </div>
          <div className="recording-candidate-name">Recording</div>
        </div>
      </div>

      {/* Proctoring Violation Alerts */}
      {violations.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            maxWidth: '500px',
            width: '90%'
          }}
        >
          {violations.map((violation, index) => (
            <div
              key={`${violation.timestamp}-${index}`}
              style={{
                backgroundColor: violation.type === 'tab_switch' ? '#d13438' : '#f7630c',
                color: 'white',
                padding: '16px 20px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                fontSize: '15px',
                fontWeight: '600',
                textAlign: 'center',
                animation: 'slideDown 0.3s ease-out'
              }}
            >
              ⚠️ {violation.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VideoProctoring;
